import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { SEO } from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { CONTACT } from '../../constants/images';

export const KidneyDialysis = () => {
  const services = [
    {
      title: 'Dialysis Access Salvage — AV Fistula Procedures',
      description: 'An AV fistula is the preferred long-term dialysis access. Over time, fistulas develop stenosis, thrombosis, or inadequate flow, making dialysis sessions inefficient or impossible.',
      symptoms: ['Inadequate blood flow during dialysis', 'Prolonged bleeding from needle sites', 'Arm or hand swelling', 'Loss of thrill over fistula', 'High venous pressures — machine alarms'],
      treatments: ['AV Fistula Angioplasty (Fistuloplasty) — balloon restores flow', 'Declotting — Pharmacomechanical Thrombectomy', 'Permcath / Tunnelled Dialysis Catheter', 'Most patients resume dialysis within 24 hours']
    },
    {
      title: 'Urinary Obstruction — PCN & Ureteric Stenting',
      description: 'The ureter can be blocked by kidney stones, strictures, or tumours — causing hydronephrosis, severe pain, and progressive kidney damage.',
      symptoms: ['Severe flank or back pain', 'Reduced or absent urine output', 'Fever and sepsis if infection develops', 'Swollen kidney on imaging'],
      treatments: ['PCN (Percutaneous Nephrostomy) — drainage tube into kidney', 'Ureteric Stenting (Antegrade) — double-J stent', 'Done under local anaesthesia']
    },
    {
      title: 'Renovascular Hypertension — Renal Artery Angioplasty',
      description: 'In some patients, persistently high blood pressure is caused by narrowing of the renal arteries. Often resistant to multiple antihypertensive medications.',
      symptoms: ['Uncontrolled blood pressure', 'BP requiring 3+ anti-hypertensive drugs', 'Headache from refractory hypertension'],
      treatments: ['Percutaneous Transluminal Renal Angioplasty (PTRA)', 'Stent placement in selected cases', 'Often dramatic BP reduction and decreased medication burden']
    }
  ];

  const faqs = [
    { q: 'How do I know if my AV fistula needs treatment?', a: 'Warning signs: reduced dialysis flow rates, arm swelling, loss of thrill over the fistula, prolonged bleeding, or machine alarms. Contact Dr. Punit Mahajan promptly.' },
    { q: 'Can a fully clotted fistula be saved without surgery?', a: 'In many cases, yes. Pharmacomechanical thrombectomy through a catheter restores fistula function without creating a new surgical fistula.' },
    { q: 'What is a PCN and when is it needed?', a: 'A PCN is a drainage tube placed into the kidney under imaging guidance to bypass a ureteric blockage. Done under local anaesthesia.' },
    { q: 'How long does AV fistula angioplasty take?', a: 'The procedure typically takes 30–60 minutes. Most patients resume their next dialysis session without interruption.' }
  ];

  return (
    <>
      <SEO 
        title="AV Fistula Salvage & Kidney Interventions in Mumbai | Dr. Punit Mahajan"
        description="AV fistula angioplasty, fistuloplasty, declotting, PCN insertion, ureteric stenting & renal artery angioplasty in Mumbai by Dr. Punit Mahajan."
      />

      <section className="bg-gradient-to-br from-blue-950 to-slate-900 text-white py-20 relative overflow-hidden"><div className="absolute inset-0 pattern-hero opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 animate-text-reveal">Kidney & Dialysis Access Interventions</h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto animate-text-reveal" style={{ animationDelay: "300ms" }}>Keep the Lifeline Working — Mumbai</p>
        </div>
      </section>

      <section className="py-16 bg-cream-textured relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose max-w-none mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Introduction</h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              For hundreds of thousands of Indians on regular haemodialysis, a functioning AV fistula is a lifeline. When it fails, dialysis becomes impossible. Dr. Punit Mahajan specialises in minimally invasive procedures to salvage failing dialysis access, relieve urinary obstruction, and treat vascular causes of uncontrolled high blood pressure.
            </p>
          </div>

          <div className="space-y-8">
            {services.map((service, index) => (
              <Card key={index} className="card-gold-hover transition-all bg-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-blue-900 mb-4">{service.title}</h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Symptoms:</h4>
                      <ul className="space-y-2">
                        {service.symptoms.map((s, idx) => (
                          <li key={idx} className="flex items-start"><CheckCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" /><span className="text-slate-600">{s}</span></li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Treatments:</h4>
                      <ul className="space-y-2">
                        {service.treatments.map((t, idx) => (
                          <li key={idx} className="flex items-start"><ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" /><span className="text-slate-600">{t}</span></li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-cream-stripes relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <Card key={idx} className="border-l-4 border-amber-500 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-blue-900 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold">Save Your Fistula — Save Your Dialysis</h2>
          <p className="text-xl text-slate-300">Early intervention has a significantly higher success rate</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${CONTACT.phone}`}><Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white">Call {CONTACT.phone}</Button></a>
            <Link to="/contact"><Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">Book Consultation</Button></Link>
          </div>
        </div>
      </section>
    </>
  );
};
